import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PawLogo } from '../shared/PawLogo';
import { LanguageSwitch } from '../shared/LanguageSwitch';

// ============================================================================
// Icons (inline SVG to avoid dependency issues with lucide-react)
// ============================================================================

const PawIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="4" r="2" />
    <circle cx="18" cy="8" r="2" />
    <circle cx="4" cy="8" r="2" />
    <path d="M11.5 13.5c-2-2-5.5-1-5.5 2 0 4 5.5 6 5.5 6s5.5-2 5.5-6c0-3-3.5-4-5.5-2z" />
  </svg>
);

const TestTubeIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2" />
    <path d="M8.5 2h7" />
    <path d="M14.5 16h-5" />
  </svg>
);

const HeartIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const ClipboardIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </svg>
);

const StethoscopeIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
    <circle cx="20" cy="10" r="2" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const BellIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const ShieldIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// ============================================================================
// Landing Page Component
// ============================================================================

export const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const scrollToFeatures = useCallback(() => {
    const el = document.getElementById('how-it-works');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ================================================================ */}
      {/* Navigation Bar */}
      {/* ================================================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-bar">
        {/* On a phone the language switch takes its own row under the logo and
            buttons: "Iniciar sesión" and "Crear cuenta" leave no room for it beside
            them at 360px. The brand name sits in a one-line box that clips: when
            there is no room beside the logo it wraps out of sight (still read by
            screen readers), so it shows at whatever width it fits. */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-1 sm:pb-0 flex flex-wrap sm:flex-nowrap items-center gap-x-2">
          <div className="h-14 sm:h-16 flex-1 min-w-0 flex items-center">
            <div className="h-8 min-w-0 overflow-hidden flex flex-wrap content-start items-center gap-x-2 sm:gap-x-2.5">
              <div className="w-8 h-8 shrink-0 bg-sage-600 rounded-xl flex items-center justify-center shadow-xs">
                <PawLogo className="w-5 h-5" color="white" faceColor="#6D28A8" />
              </div>
              <span className="text-base sm:text-lg font-bold text-charcoal leading-8 whitespace-nowrap">{t('landing.nav.brandName')}</span>
            </div>
          </div>
          <LanguageSwitch className="order-last w-full justify-end sm:order-none sm:w-auto" />
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-mid hover:text-charcoal transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-ivory whitespace-nowrap"
            >
              {t('landing.nav.login')}
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm font-semibold text-white bg-sage-600 hover:bg-sage-700 transition-all duration-200 px-3 sm:px-4 py-2 rounded-xl shadow-soft hover:shadow-card active:scale-[0.98] whitespace-nowrap"
            >
              {t('landing.nav.signup')}
            </button>
          </div>
        </div>
      </nav>

      {/* ================================================================ */}
      {/* Hero Section */}
      {/* ================================================================ */}
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-sage-50/60 to-white">
        <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-charcoal leading-tight mb-4 sm:mb-6 tracking-tight">
            {t('landing.hero.heading')}
          </h1>
          <p className="text-lg sm:text-xl text-soft leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-10">
            {t('landing.hero.subheading')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-sage-600 hover:bg-sage-700 rounded-xl transition-all duration-200 shadow-soft hover:shadow-card active:scale-[0.98] min-h-[48px]"
            >
              {t('landing.hero.getStarted')}
            </button>
            <button
              onClick={scrollToFeatures}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-6 py-3.5 text-base font-medium text-sage-700 hover:text-sage-800 hover:bg-sage-50 rounded-xl transition-all duration-200 min-h-[48px]"
            >
              {t('landing.hero.learnMore')}
              <ChevronDownIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* How It Works */}
      {/* ================================================================ */}
      <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-charcoal text-center mb-4">
            {t('landing.howItWorks.heading')}
          </h2>
          <p className="text-soft text-center mb-12 sm:mb-16 max-w-lg mx-auto">
            {t('landing.howItWorks.subheading')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-2xl flex items-center justify-center">
                <PawIcon className="w-7 h-7 text-sage-700" />
              </div>
              <div className="text-xs font-semibold text-sage-600 uppercase tracking-wider mb-2">{t('landing.howItWorks.step1.label')}</div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.howItWorks.step1.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.howItWorks.step1.body')}
              </p>
            </div>
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-2xl flex items-center justify-center">
                <HeartIcon className="w-7 h-7 text-sage-700" />
              </div>
              <div className="text-xs font-semibold text-sage-600 uppercase tracking-wider mb-2">{t('landing.howItWorks.step2.label')}</div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.howItWorks.step2.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.howItWorks.step2.body')}
              </p>
            </div>
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-2xl flex items-center justify-center">
                <ClipboardIcon className="w-7 h-7 text-sage-700" />
              </div>
              <div className="text-xs font-semibold text-sage-600 uppercase tracking-wider mb-2">{t('landing.howItWorks.step3.label')}</div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.howItWorks.step3.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.howItWorks.step3.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Key Features */}
      {/* ================================================================ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-charcoal text-center mb-12 sm:mb-16">
            {t('landing.features.heading')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-faint hover:shadow-lifted transition-all duration-300">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center mb-4">
                <TestTubeIcon className="w-5 h-5 text-sage-700" />
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.features.staging.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.features.staging.body')}
              </p>
            </div>
            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-faint hover:shadow-lifted transition-all duration-300">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center mb-4">
                <ClockIcon className="w-5 h-5 text-sage-700" />
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.features.dailyTracking.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.features.dailyTracking.body')}
              </p>
            </div>
            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-faint hover:shadow-lifted transition-all duration-300">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <BellIcon className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.features.alerts.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.features.alerts.body')}
              </p>
            </div>
            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-faint hover:shadow-lifted transition-all duration-300">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center mb-4">
                <StethoscopeIcon className="w-5 h-5 text-sage-700" />
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">{t('landing.features.vetPrep.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.features.vetPrep.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Trust Section */}
      {/* ================================================================ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-charcoal text-center mb-12 sm:mb-16">
            {t('landing.trust.heading')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <ShieldIcon className="w-6 h-6 text-green-700" />
              </div>
              <h3 className="text-base font-semibold text-charcoal mb-2">{t('landing.trust.iris.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.trust.iris.body')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-sage-100 rounded-full flex items-center justify-center">
                <HeartIcon className="w-6 h-6 text-sage-700" />
              </div>
              <h3 className="text-base font-semibold text-charcoal mb-2">{t('landing.trust.notReplacement.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.trust.notReplacement.body')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <LockIcon className="w-6 h-6 text-purple-700" />
              </div>
              <h3 className="text-base font-semibold text-charcoal mb-2">{t('landing.trust.data.title')}</h3>
              <p className="text-soft text-sm leading-relaxed">
                {t('landing.trust.data.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Footer CTA */}
      {/* ================================================================ */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-sage-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-charcoal mb-4">
            {t('landing.cta.heading')}
          </h2>
          <p className="text-mid mb-8 text-base sm:text-lg">
            {t('landing.cta.body')}
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-sage-600 hover:bg-sage-700 rounded-xl transition-colors shadow-sm min-h-[48px]"
          >
            {t('landing.cta.getStarted')}
          </button>
          <p className="mt-4 text-sm text-soft">
            {t('landing.cta.free')}
          </p>
          <p className="mt-1 text-sm text-soft">
            {t('landing.cta.setupTime')}
          </p>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Footer */}
      {/* ================================================================ */}
      <footer className="py-8 px-4 sm:px-6 border-t border-faint">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-sage-600 rounded-md flex items-center justify-center">
              <PawLogo className="w-3.5 h-3.5" color="white" faceColor="#6D28A8" />
            </div>
            <span className="text-sm text-soft">{t('landing.footer.brandName')}</span>
          </div>
          <p className="text-xs text-soft text-center sm:text-right max-w-md">
            {t('landing.footer.disclaimer')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
